import { create } from "zustand";
import axios from "axios";


// const API_URL = "https://storyevaluator-backend.onrender.com/api/auth";
axios.defaults.withCredentials = true;

export const useAuthStore = create((set) => ({
  user: null,
  isAuthenticated: false,
  error: null,
  isLoading: false,
  isCheckingAuth: true,
  message: null,
  credits: 0, // Track credits

  signup: async (email, password, name) => {
    set({ isLoading: true, error: null });
    const maxRetries = 3; // Reduced retry count
    let attempts = 0;

    while (attempts < maxRetries) {
      try {
        const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/signup`, { email, password, name });
        set({ user: response.data.user, isAuthenticated: true, isLoading: false, credits: 15 });
        return;
      } catch (error) {
        if (error.code === "ECONNRESET") {
          attempts++;
          await new Promise((resolve) => setTimeout(resolve, 1000)); // Add delay
          continue;
        }
        set({ error: error.response?.data?.message || "Error signing up", isLoading: false });
        throw error;
      }
    }
  },

  login: async (email, password) => {
    set({ isLoading: true, error: null });

    if (!email || !password) {
      set({ error: "All fields are required", isLoading: false });
      return; // Stop further execution
    }

    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, { email, password });
      set({
        isAuthenticated: true,
        user: response.data.user,
        error: null,
        isLoading: false,
        credits: response.data.user.credits, // Initialize credits from server
      });
    } catch (error) {
      set({ error: error.response?.data?.message || "Error logging in", isLoading: false });
    //   throw error;
    }
  },

  logout: async () => {
    set({ isLoading: true, error: null });
    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/logout`);
      set({ user: null, isAuthenticated: false, error: null, isLoading: false, credits: 0 });
    } catch (error) {
      set({ error: "Error logging out", isLoading: false });
      throw error;
    }
  },

  updateCredits: (newCredits) => {
    set((state) => ({
      credits: newCredits,
      user: { ...state.user, credits: newCredits },
    }));
  },

  consumeCredits: (creditsToDeduct) => {
    set((state) => {
      if (state.credits < creditsToDeduct) {
        return { error: "Not enough credits!" };
      }
      return {
        credits: state.credits - creditsToDeduct,
        user: { ...state.user, credits: state.credits - creditsToDeduct },
      };
    });
  },

  verifyEmail: async (code) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/verify-email`, { code });
      set({ user: response.data.user, isAuthenticated: true, isLoading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.message || "Error verifying email", isLoading: false });
      throw error;
    }
  },

  resendCode: async (email) => {
    set({ isLoading: true, error: null });
    try {
      // Replace with your API endpoint to resend the verification code
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/resend-code`, { email });
      set({ isAuthenticated: true, isLoading: false });
      return response.data;
    } catch (error) {
      if (error.response?.status === 404) {
        
        // Set the error message and prepare for redirect
        set({
          user: null,
          isLoading: false,
          isAuthenticated: false,
          error: error.response?.data?.message || "Failed to resend code",
        });
        throw error;
  
      } else {
        set({ isLoading: false, error: error.response?.data?.message || "Failed to resend code" });
        throw error;
      }
    }
  },
  

  checkAuth: async () => {
    set({ isCheckingAuth: true, error: null });
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/auth/check-auth`);
      set({
        user: response.data.user,
        isAuthenticated: true,
        isCheckingAuth: false,
        credits: response.data.user.credits, // Initialize credits from server
      });
    } catch (error) {
      if (error.response?.status === 401) {
        set({ error: null, isCheckingAuth: false, isAuthenticated: false });
      } else {
        set({ error: error.response?.data?.message || "Server error", isCheckingAuth: false });
      }
    }
  },

  forgotPassword: async (email) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/forgot-password`, { email });
      set({ message: response.data.message, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || "Error sending reset password email",
      });
      throw error;
    }
  },

  resetPassword: async (token, password) => {
    set({ isLoading: true, error: null });
    try {
      const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/auth/reset-password/${token}`, { password });
      set({ message: response.data.message, isLoading: false });
    } catch (error) {
      set({
        isLoading: false,
        error: error.response?.data?.message || "Error resetting password",
      });
      throw error;
    }
  },

  clearError: () => set({ error: null }),
}));
